<script setup>
import { appList } from "@/AppList.js";
import axios from "axios";
import { computed, onMounted, reactive, ref } from "vue";

const apps = reactive([]);
const searchQuery = ref("");
const favoritedApps = ref([]);
const favoriteTags = ref([]);

function customSort(a, b) {
  if (a.isFavorited && !b.isFavorited) {
    return -1; // a comes before b
  } else if (!a.isFavorited && b.isFavorited) {
    return 1; // b comes before a
  } else {
    //if a has tags in favoriteTags and b doesn't, a comes before b
    if (
      a.tags.split(",").some((tag) => favoriteTags.value.includes(tag)) &&
      !b.tags.split(",").some((tag) => favoriteTags.value.includes(tag))
    ) {
      return -1;
    }
    //if b has tags in favoriteTags and a doesn't, b comes before a
    else if (
      !a.tags.split(",").some((tag) => favoriteTags.value.includes(tag)) &&
      b.tags.split(",").some((tag) => favoriteTags.value.includes(tag))
    ) {
      return 1;
    }
    //if a and b have tags in favoriteTags, sort alphabetically
    else if (
      a.tags.split(",").some((tag) => favoriteTags.value.includes(tag)) &&
      b.tags.split(",").some((tag) => favoriteTags.value.includes(tag))
    ) {
      return a.title.localeCompare(b.title);
    }
    //if neither a nor b have tags in favoriteTags, sort alphabetically
    else {
      return a.title.localeCompare(b.title);
    }
  }
}

const searchedApps = computed(() => {
  return apps
    .filter((app) => {
      return (
        app.tags.toLowerCase().indexOf(searchQuery.value.toLowerCase()) != -1
      );
    })
    .sort(customSort);
});

function getCookie(cname) {
  var name = cname + "=";
  var ca = document.cookie.split(";");
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) == " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

const HTTP_PREFERENCES = axios.create({
  baseURL: process.env.VUE_APP_USER_PREFERENCES_API_URL,
  timeout: 10000,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
    "X-XSRF-TOKEN": getCookie("XSRF-TOKEN"),
  },
  withCredentials: true,
});

HTTP_PREFERENCES.interceptors.request.use(
  (config) => {
    // Add user's Otka token to request
    let token = JSON.parse(localStorage.getItem("okta-token-storage"))[
      "accessToken"
    ]["accessToken"];
    config.headers["Authorization"] = `Bearer ${token}`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const usageTracker = (card) => {
  const title = card.title.replace(" ", "-");
  HTTP_PREFERENCES.get(`/service/bridge/logging/${title}`);
};

const onFavClick = (card) => {
  // Check if app.isFavorited is already true in apps
  // If it is, then set it to false and delete from favoritedApps
  // If it isn't, then set it to true and add to favoritedApps
  const appIndex = apps.findIndex((appItem) => appItem.title === card.title);
  if (apps[appIndex].isFavorited) {
    HTTP_PREFERENCES.delete(`/bridge/${card.title}`);
    apps[appIndex].isFavorited = false;
  } else {
    HTTP_PREFERENCES.post("/bridge", { title: card.title });
    apps[appIndex].isFavorited = true;
    favoriteTags.value = favoriteTags.value.concat(card.tags.split(","));
  }
};

const navigate = (card) => {
  window.open(card.link, "_blank");
};

onMounted(async () => {
  // For each item in appList added it to apps with the key being app.title

  appList.forEach((element) => {
    element["isFavorited"] = false;
    apps.push(element);
  });

  favoritedApps.value = await HTTP_PREFERENCES.get("/bridge");

  for (let i = 0; i < searchedApps.value.length; i++) {
    const app = searchedApps.value[i];
    if (
      favoritedApps.value.data.some((favoritedApp) => {
        let tokenizedSk = favoritedApp.sk.split("#");
        return tokenizedSk[2] === app.title;
      })
    ) {
      const appIndex = apps.findIndex((appItem) => appItem.title === app.title);
      apps[appIndex].isFavorited = true;
      // concat teags array to favoriteTags ref array
      favoriteTags.value = favoriteTags.value.concat(
        apps[appIndex].tags.split(",")
      );
    }
  }
  HTTP_PREFERENCES.get(`/service/bridge/logging/home`);
});
</script>

<template>
  <title>FIG Bridge</title>
  <div class="okta-container">
    <div class="row-1">
      <div class="current-app-container" style="height: 45px !important">
        <span class="platform-title">BRIDGE</span>
        <hr class="horizontal" />
        <span class="analytic-tool">{{ current_page }}</span>
      </div>
      <div class="personal-info-container">
        <div class="current-user">
          <span class="current-user-name pr-2">{{ currentUserName }}</span>
          <span class="current-user-title pr-2">{{ currentUserTitle }}</span>
        </div>
      </div>
    </div>
  </div>
  <img class="logo-head" src="@/assets/VG_logo.svg" />
  <div class="header">
    <span>
      <div class="header-title">
        <img
          style="padding-right: 200px"
          class="bg-image"
          src="@/assets/bg-vector2.png"
          alt="Image"
        />
        <img
          style="padding-left: 200px"
          class="bg-image"
          src="@/assets/bg-vector.png"
          alt="Image"
        />
        <img
          style="padding-left: 1080px"
          class="bg-image-right"
          src="@/assets/right.png"
          alt="Image"
        />
        <img class="header-image" src="@/assets/Vector.png" alt="Image" /><span
          style="z-index: 1"
        >
          BRIDGE</span
        >
      </div>
    </span>

    <div class="search-bar-container">
      <input class="search-bar" v-model="searchQuery" placeholder="Search" />
      <div class="search-icon">
        <img src="@/assets/bridge-search-icon.svg" />
      </div>
    </div>
  </div>
  <div class="card-container">
    <div
      class="card"
      v-for="card in searchedApps"
      :key="card.title"
      @click="
        navigate(card);
        usageTracker(card);
      "
    >
      <div class="container" style="font-family: 'Mark Pro'">
        <div class="title-line">
          <h2 class="title">{{ card.title }}</h2>
          <button class="fav-button" @click.stop="onFavClick(card)">
            <div
              class="star"
              :class="{ 'favorited-star': card.isFavorited }"
            ></div>
          </button>
        </div>
        <h4 class="card-text">{{ card.text }}</h4>
        <div class="owner-text">
          <h4 style="font-weight: 400">
            <span class="owner-label">Owner:</span>
            <br />
            {{ card.owner }}
          </h4>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.title-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 40px;
}
.platform-title {
  font-family: Mark Pro;
  font-weight: 700;
  padding-left: 10px;
  font-size: 20px;
  color: #666666;
}
.header {
  overflow: hidden;
  background-color: #007873;
  height: 162px;
  position: relative;
}

body {
  margin: 0px !important;
}

.header-image {
  padding-right: 15px;
}
.fav-button {
  width: 16px;
  height: 16px;
  font-size: 40px;
  background-color: transparent;
  border: none;
  cursor: pointer;
}

.star {
  width: 16px;
  height: 16px;
  color: gray;
  background-color: gray;
  -webkit-mask: url(@/assets/StarWt.svg) no-repeat center;
  mask: url(@/assets/StarWt.svg) no-repeat center;
}

.favorited-star {
  color: gold !important;
  background-color: gold !important;
}

.header-title {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 50px;
  font-family: Mark Pro;
  font-weight: 700;
  color: white;
  padding: 10px;
  position: relative;
  z-index: 1;
}
.bg-image {
  width: auto;
  height: 163px;
  margin-right: 10px;
  position: absolute;
  top: 97%;
  transform: translateY(-50%);
  z-index: -1;
}

.bg-image-right {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 1000px;
  height: 240px;
  z-index: -1;
}

.card {
  /* Add shadows to create the "card" effect */
  position: relative;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);
  transition: 0.3s;
  width: 440px;
  height: 250px;
  padding: 10px;
  margin: 10px;
  background-color: #333;
  color: #fff;
}
.card-text {
  font-size: 17px;
  font-weight: 400;
  color: #cbcece;
}
.card-link {
  text-decoration: none;
  color: inherit;
}

.card-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-left: auto;
  margin-right: auto;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.title {
  font-size: 21px;
  font-weight: 700;
}
.search-bar {
  position: relative;
  display: block;
  width: 847px;
  height: 48px;
  font-size: 18px;
  z-index: 1;
  font-family: Mark Pro;
  color: #757575;
  text-indent: 15px;
  border-top-right-radius: 0px;
  border-top-left-radius: 0px;
  border: 1px solid black;
}

.search-bar-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}
.owner-label {
  font-weight: 700;
  color: #cbcece;
}
.owner-text {
  position: absolute;
  bottom: 10px;
  left: 10px;
  padding-bottom: 5px;
  padding-left: 20px;
  font-size: 17px;
  color: #cbcece;
}
.search-icon {
  height: 52px;
  z-index: 1;
}
.svg-search-icon {
  height: 48px;
}
.logo {
  margin-left: auto;
  margin-right: auto;
  display: block;
  width: 50%;
  height: 50%;
  margin: 10px;
}

.logo-icon {
  width: 100;
  height: 100;
}

.logo-head {
  padding-left: 10px;
}

/* On mouse-over, add a deeper shadow */
.card:hover {
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.2);
  outline: 1px solid black;
  background-color: white;
  color: black;
}
.card:hover .card-text {
  color: black !important;
}

.card:hover .owner-label {
  color: black !important;
}

.card:hover .owner-text {
  color: black !important;
}
/* Add some padding inside the card container */
.container {
  padding: 2px 16px;
}

@media screen and (max-width: 500px) {
  .header-title {
    float: none;
  }
}
</style>
